// V1.1-Beta — Lightweight in-house analytics tracker.
//
// Sends `POST /api/events` for a fixed allow-list of events. Sessions are
// scoped to the browser tab (sessionStorage). Failed sends are silently
// swallowed — analytics MUST NEVER block or break the UX.
//
// Public surface:
//   trackEvent(eventName, props?, { pageUrl?, referrer? })
//   trackPageView(pathname, { referrer? })
//   getSessionId()
//
// Usage:
//   import { trackEvent, trackPageView } from "@/lib/analytics";
//   trackEvent("signup_form_submit");

import { api } from "@/api/client";

const STORAGE_KEY = "iaos_analytics_session";

// V1.1-Beta Launch Prep — UTM acquisition attribution.
const UTM_STORAGE_KEY = "iaos_utm_attribution";
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

const ALLOWED_EVENT_NAMES = new Set([
  "page_view",
  "landing_view_hero",
  "signup_form_view",
  "signup_form_submit",
  "signup_success",
  "login_form_view",
  "login_success",
  "profile_wizard_step_view",
  "profile_wizard_complete",
  "readiness_view",
  "credit_report_upload_start",
  "credit_report_upload_success",
  "dispute_letter_generate",
  "partner_apply_click",
  "feedback_widget_open",
  "feedback_widget_submit",
  "beta_code_validate",
  "account_created",
  "beta_code_used",
  // V1.1-Beta — public /beta landing page form submission.
  "beta_access_requested",
  // Admin console — Phase Stripe-4.
  "Revenue Dashboard Viewed",
]);

function _readSession() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function _writeSession(id) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* sessionStorage unavailable — give up silently */
  }
}

function _generateSessionId() {
  // crypto.randomUUID() is widely supported; fall back to a Math.random suffix
  // for headless/older browsers (e.g. Playwright Webkit < 17).
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `s_${crypto.randomUUID().replace(/-/g, "")}`;
    }
  } catch {
    /* ignore */
  }
  return `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export function getSessionId() {
  let id = _readSession();
  if (!id) {
    id = _generateSessionId();
    _writeSession(id);
  }
  return id;
}

/**
 * V1.1-Beta Launch Prep — capture + persist UTM params on first visit.
 * Reads `utm_*` params from the current URL, MERGES them on top of the
 * previously-stored attribution dict (so a re-mount with only some params
 * doesn't clobber the full set), and returns the merged dict.
 *
 * Recurrence #4 fix: a previous version OVERWROTE sessionStorage whenever
 * the URL contained any UTM. That meant a Signup re-mount with e.g. only
 * `?utm_content=ep-12` (browser autocomplete drop, partial share link,
 * router replace) wiped utm_source/medium/campaign from the cache. Now
 * each key is upserted individually — stored values survive partial URL
 * states, and the URL still wins per-key when present.
 */
export function captureUtmFromUrl() {
  if (typeof window === "undefined") return {};
  try {
    const sp = new URLSearchParams(window.location.search);
    // Load the previously-stored set first, then upsert per-key from the URL.
    let merged = {};
    try {
      const cached = window.sessionStorage.getItem(UTM_STORAGE_KEY);
      if (cached) merged = JSON.parse(cached) || {};
    } catch { /* ignore */ }
    let touched = false;
    UTM_PARAMS.forEach((k) => {
      const v = sp.get(k);
      if (v) {
        merged[k] = v;
        touched = true;
      }
    });
    if (touched) {
      window.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(merged));
    }
    return merged;
  } catch {
    return {};
  }
}

/**
 * V1.1 Invite Performance regression fix — read UTM attribution at submit time
 * from BOTH the current URL AND sessionStorage. URL wins when present so that
 * an in-tab navigation to /signup?utm_*=… still attributes correctly even if
 * the `captureUtmFromUrl()` useEffect hasn't yet run, or sessionStorage is
 * disabled (private browsing, third-party cookie blockers, etc.).
 */
export function resolveUtm() {
  if (typeof window === "undefined") return {};
  let urlUtm = {};
  try {
    const sp = new URLSearchParams(window.location.search);
    UTM_PARAMS.forEach((k) => {
      const v = sp.get(k);
      if (v) urlUtm[k] = v;
    });
  } catch { /* ignore */ }
  let storedUtm = {};
  try {
    const cached = window.sessionStorage.getItem(UTM_STORAGE_KEY);
    storedUtm = cached ? JSON.parse(cached) : {};
  } catch { /* ignore */ }
  // URL takes precedence over session cache.
  return { ...storedUtm, ...urlUtm };
}

export function getStoredUtm() {
  if (typeof window === "undefined") return {};
  try {
    const cached = window.sessionStorage.getItem(UTM_STORAGE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
}

export async function trackEvent(eventName, eventProps = {}, opts = {}) {
  if (!ALLOWED_EVENT_NAMES.has(eventName)) {
    // Log a console warning in dev; never crash callers.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(`[analytics] Unknown event_name: ${eventName}`);
    }
    return;
  }
  const sessionId = getSessionId();
  // V1.1-Beta Launch Prep — auto-merge stored UTM attribution into props.
  const utm = getStoredUtm();
  const body = {
    session_id: sessionId,
    event_name: eventName,
    event_props: { ...utm, ...(eventProps || {}) },
    page_url: opts.pageUrl || (typeof window !== "undefined" ? window.location.pathname + window.location.search : null),
    referrer: opts.referrer || (typeof document !== "undefined" ? document.referrer : null) || null,
  };
  try {
    await api.post("/events", body, { timeout: 4000 });
  } catch {
    /* swallow — analytics must never break the UX */
  }
}

export function trackPageView(pathname, opts = {}) {
  return trackEvent("page_view", { pathname }, { pageUrl: pathname, ...opts });
}

export const ANALYTICS_EVENTS = ALLOWED_EVENT_NAMES;
